import { CircularProgress, Stack, Typography } from '@mui/material';
import classnames from 'classnames';
import MonitoringInterval from 'js/components/data_sources/add_ds_form/MonitoringInterval/MonitoringInterval';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';
import { SUPPORT_KEY_SOURCE_FORM_NOSQL } from 'js/constants/supportPageKeys';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import JSONUtils from 'js/lib/JSONUtils';
import { debounce, filter, find, forEach, get, isEmpty, map, omit } from 'lodash';
import { Button } from 'new/elements/Button';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import React from 'react';
import UIUtils from '../../../../lib/UIUtils';
import LoadableMonacoEditor from '../../../elements/editors/monaco/LoadableMonacoEditor';
import { getCurrentSelectedOption } from '../../../elements/rselect/ReactSelectUtils';
import DataSourceDetails from '../DataSourceDetails/DataSourceDetails';
import DatabaseList from './DatabaseList';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { EmptyView } from 'new/elements/EmptyView';
import SelectedDatabaseInputs from 'js/components/data_sources/add_ds_form/SelectedDatabaseInputs/SelectedDatabaseInputs';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { UiMode } from 'new/constants/uiMode';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';

/**
 * Styles
 */
import './NoSQLConfigurator.scss';
import './DatabaseConfigurator.scss';
import styles from './NoSQLConfigurator.module.scss';

const navOptions = [
  { label: 'Select a Collection', id: 'select-collection' },
  { label: 'Filter Collections', id: 'filter-collection' },
  { label: 'Schedule Ingestion', id: 'scheduling-section' },
];

class NoSQLConfigurator extends React.Component {
  state = {
    databasesIds: [],
    databases: {},
    collections: {},
    selectedDatabaseId: null,
    expandedCollectionId: null,
    collectionsForDatabase: {},
  };

  componentDidMount() {
    //Fetch probe tree for this data source
    this.onRequestTree();

    if (this.props.probeData) {
      this.initializeState(this.props);
    }

    this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_NOSQL);
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_NOSQL);
  }

  componentDidUpdate(prevProps, prevState) {
    let { selectedCredentialId } = this.props;
    if (prevProps.selectedCredentialId !== selectedCredentialId) {
      this.props.onRequestProbeTree();
    }

    const collectionId = this.props.selectedCollection && this.props.selectedCollection.id;

    if (
      collectionId &&
      prevState.collections[collectionId] &&
      this.state.collections[collectionId] &&
      !prevState.collections[collectionId].scanned &&
      this.state.collections[collectionId].scanned
    ) {
      this.selectCollection(collectionId);
    }
    if (this.props.probeData && this.props.probeData !== prevProps.probeData) {
      this.initializeState(this.props);
    }
  }

  initializeState = (props) => {
    let databasesIds = [],
      databases = {},
      collections = {},
      collectionsForDatabase = {};
    forEach(props.probeData, (d) => {
      let database = {
        id: d.id,
        name: d.name,
      };
      databasesIds.push(d.id);
      databases[d.id] = database;
      collectionsForDatabase[d.id] = [];
      forEach(d.children, (c) => {
        let collection = {
          id: c.id,
          name: c.name,
          database: d.name,
          scanned: c.scanned,
        };
        collections[c.id] = collection;
        collectionsForDatabase[d.id].push(c.id);
      });
    });
    this.setState(
      {
        databasesIds,
        databases,
        collections,
        collectionsForDatabase,
        selectedDatabaseId: props.selectedDatabase || this.state.selectedDatabaseId,
      },
      () => {
        // initialize state from the source being edited
        const { sourceBeingEdited, sourceConfig } = props;

        if (sourceBeingEdited && sourceConfig.collection) {
          const { database, collection } = sourceConfig;
          const collectionId = `${database || databasesIds[0]}/${collection}`;

          if (!isEmpty(collections)) {
            this.selectCollection(collectionId);
            //TODO: pass collection config
            this.updateCollection(collectionId, {});
          }
        }
      },
    );
  };

  onChangeTableInput = (tId) => {
    if (tId.length === 0) {
      this.setState(
        {
          expandedCollectionId: '',
        },
        () => {
          this.props.onCollectionSelected(null);
        },
      );
    }

    if (tId) {
      const collection = this.state.collections?.[tId];
      if (!collection) {
        this.props.onCollectionSelected({
          id: tId,
          name: tId?.split('/')[1],
          database: tId?.split('/')[0],
          scanned: false,
        });
        this.setState({
          expandedCollectionId: tId,
        });
      } else {
        this.probeNode(tId);
        this.setState(
          {
            expandedCollectionId: tId,
          },
          () => {
            this.props.onCollectionSelected(collection);
          },
        );
      }
    }
  };

  selectDatabase = (dId) => {
    if (dId === this.state.selectedDatabaseId) {
      this.setState(
        {
          selectedDatabaseId: null,
        },
        () => {
          this.props.onCollectionSelected(null);
        },
      );
    } else {
      this.probeNode(dId);
      this.setState(
        {
          selectedDatabaseId: dId,
        },
        () => {
          this.props.onCollectionSelected(null);
        },
      );
    }
  };

  expandCollection = (collectionId) => {
    //TODO: for future implementation, if required to show some collection information
    // this.probeNode(collectionId);
    // this.setState({
    //   expandedCollectionId: collectionId
    // });
  };

  selectCollection = (collectionId, callback) => {
    if (collectionId) {
      let collection = this.state?.collections?.[collectionId] || {};
      if (!isEmpty(collection)) {
        this.setState(
          {
            expandedCollectionId: collectionId,
            selectedDatabaseId: collection.database,
          },
          () => {
            this.props.onCollectionSelected(collection);
            callback?.();
          },
        );
      }
    }
  };

  updateCollection = (collectionId, collectionConfig) => {
    let collection;
    if (collectionId === (this.props.selectedCollection || {}).id) {
      collection = this.props.selectedCollection;
    } else {
      collection = this.state.collections[collectionId];
    }
    if (collection) {
      //TODO: gray area, not sure config required...
      forEach(collectionConfig, (value, key) => {
        if (collection[key] === value) {
          delete collection[key];
        } else {
          collection[key] = value;
        }
      });
    }
    if (!isEmpty(collection)) {
      this.props.onCollectionSelected(collection);
    }
  };

  probeNode = (nodeId) => {
    const node = this.props.probeDataFlat?.[nodeId];
    if (node && !node.scanned && !this.props.isLoadingProbeData) {
      this.props.onRequestProbeTree(false, node);
    }
  };

  onChange = (name, value, data = undefined) => {
    const { sourceConfig } = this.props;
    this.props.onSourceConfigChanged(
      this.getNewConfig({
        ...sourceConfig,
        ...FlowTriggerHelper.pickScheduleFields(data),
        [name]: value,
      }),
    );
  };

  getSelectField = (field) => {
    const { sourceConfig } = this.props;
    const { options, name, required } = field;
    const currentValue = getCurrentSelectedOption(options, get(sourceConfig, name));

    return (
      <div className="input-container">
        <FieldBuilderRedesigned
          id={name}
          isClearable={false}
          backspaceRemovesValue={false}
          isSearchable
          field={field}
          labeled={false}
          currentFieldValue={currentValue}
          isRequired={required}
          onChange={this.onChange}
        />
      </div>
    );
  };

  getNewConfig = (newConfig) => {
    let { sourceConfigFields, selectedConnection } = this.props;
    let allFields = sourceConfigFields[selectedConnection];
    let notVisibleFieldNames = map(
      filter(allFields, (field) => !UIUtils.shouldRenderField(field, newConfig)),
      (field) => field.name,
    );

    return omit(newConfig, notVisibleFieldNames);
  };

  _getContent = () => {
    const { probeSample } = this.props;
    let samples = get(probeSample, '[0].response.output', '');
    if (samples && samples.errorMessage) {
      return <div className="info-text">{samples.errorMessage}</div>;
    }
    let editorContent = '';
    if (samples && samples.response && samples.contentType === 'application/json') {
      editorContent = samples.response;
    }

    return (
      <LoadableMonacoEditor
        value={editorContent}
        language="json"
        theme="dark"
        options={{
          readOnly: true,
          minimap: { enabled: false },
        }}
      />
    );
  };

  getResourceConfig = () => {
    const { source, sourceConfig } = this.props;
    const clonedSourceConfig = structuredClone(sourceConfig);
    const name = source?.name || this.props?.name;
    const description = source?.description || this.props?.description;

    delete clonedSourceConfig.name;
    delete clonedSourceConfig.sourceName;
    delete clonedSourceConfig.description;
    delete clonedSourceConfig.sourceDescription;

    return {
      name,
      description,
      source_config: { collection: null, database: null, ...clonedSourceConfig },
    };
  };

  validateRequiredFields = (config) => {
    const { sourceConfigFields, selectedConnection } = this.props;
    const fields = sourceConfigFields[selectedConnection];
    const missingFields = getEmptyRequiredFields(fields, config);
    return !missingFields.length;
  };

  onSaveAdvancedMode = async (configString) => {
    const { nextFormStage, onError = () => {}, onSaveAdvancedMode } = this.props;

    if (!JSONUtils.isJSON(configString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const { name, description, source_config } = JSON.parse(configString);

    const data = {
      ...source_config,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }
    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sourceConfig, onSourceConfigChanged } = this.props;
    if (typeof onSourceConfigChanged !== 'function') return;

    onSourceConfigChanged({ ...sourceConfig, 'ui.mode': UiMode.VIEW });
  };

  onRequestTree = () => {
    if (this.props.selectedCredentialId) {
      if (
        this.props.sourceConfig &&
        this.props.sourceConfig.database &&
        this.props.sourceConfig.collection
      ) {
        this.props.onRequestInitialDBProbeTree({
          type: 'collection',
          name: this.props.sourceConfig.collection,
          parent: this.props.sourceConfig.database,
        });
      } else {
        this.props.onRequestProbeTree();
      }
    }
  };

  render() {
    let {
      databasesIds,
      databases,
      collections,
      selectedDatabaseId,
      expandedCollectionId,
      collectionsForDatabase,
    } = this.state;
    let {
      sourceConfigFields,
      sourceBeingEdited,
      selectedCollection,
      probeData,
      probeDataFlat,
      isLoadingProbeData,
      isLoadingProbeSample,
      connectionMetas,
      selectedConnection,
      sourceConfig,
      onGetProbeSample,
      onSourceConfigChanged,
      currentOrg,
      name = '',
      description = '',
    } = this.props;

    const meta = connectionMetas[selectedConnection];
    const { connectionType } = meta;

    const databasesArray = map(databasesIds, (i) => databases[i]);
    const isQueryMode = sourceConfig?.mode === 'query';
    const fields = sourceConfigFields[selectedConnection];
    const collection = get(sourceConfig, 'collection', '');
    const content = this._getContent();

    // pull all query editor for Query mode
    const queryFields = fields.filter(
      (field) => UIUtils.shouldRenderField(field, sourceConfig) && field.type === 'editor',
    );
    const modeField = find(
      fields,
      (field) => field.name === 'mode' && UIUtils.shouldRenderField(field, sourceConfig),
    );

    const isHCEnabled = UIUtils.isHCEnabled(currentOrg.id, connectionType, true);
    let hcScaleFactorField;

    if (isHCEnabled) {
      hcScaleFactorField = find(
        fields,
        (field) =>
          field.name === 'hc_scale_factor' && UIUtils.shouldRenderField(field, sourceConfig),
      );

      if (hcScaleFactorField) {
        // disable lower HC scale factors
        const hcScaleFactorOptions = UIUtils.disableInvalidHCOptions(
          hcScaleFactorField.options,
          sourceBeingEdited &&
            sourceBeingEdited.source_config &&
            sourceBeingEdited.source_config['hc_scale_factor'],
        );

        hcScaleFactorField = {
          ...hcScaleFactorField,
          options: hcScaleFactorOptions,
        };
      }
    }

    const startCronField = fields.find(
      (field) => field.name === 'start.cron' && UIUtils.shouldRenderField(field, sourceConfig),
    );

    const timestampFieldNames = ['timestamp.key', 'timestamp.load.from', 'timestamp.load.to'];
    const timestampFields = filter(
      fields,
      (field) =>
        timestampFieldNames.includes(field.name) && UIUtils.shouldRenderField(field, sourceConfig),
    );

    if (isEmpty(probeData) && isLoadingProbeData) {
      const meta = connectionMetas[selectedConnection];
      return <ConnectionLoader message={`Connecting to ${meta.displayName}...`} loading />;
    }

    const resourceConfig = this.getResourceConfig();

    return (
      <Stack>
        <DataSourceDetails
          name={name}
          description={description}
          onInputChange={this.props.onMetaInfoChanged}
          leftView={
            <HeaderMetaConfiguration
              enableInputFields={false}
              connection={selectedConnection}
              isDestination={false}
              sourceOrDestinationChipChildren={
                <ResourcePopoverMenu
                  resourceConfig={resourceConfig}
                  connectorType={selectedConnection}
                  jsonConfigOnSave={this.onSaveAdvancedMode}
                  onSwitchUiMode={this.onSwitchUiMode}
                  resourcePersisted={!!this.props.sourceBeingEdited?.id || !!this.props.source?.id}
                  jsonConfigOnClose={() => {
                    if (this.props.goBack) {
                      this.props.goBack();
                    }
                  }}
                />
              }
            />
          }
        />
        <Stack padding={2} className={styles.galactic_sand_bg}>
          <div className="nosql">
            <div
              className={classnames(
                'database-source-configurator',
                'advanced-configuration-left-content',
                { 'is-query-mode': isQueryMode },
              )}
            >
              <NavScroller navOptions={navOptions} containerClassName="nosql-nav-container">
                <NavScrollerContext.Consumer>
                  {({ activeScroll }) => {
                    const selectCollectionClass = classnames(
                      activeScroll === 'select-collection'
                        ? [styles.item_default, styles.item_focused]
                        : [styles.item_default],
                    );

                    const filterCollectionClass = classnames(
                      activeScroll === 'filter-collection'
                        ? ['filter-collection-section', styles.item_focused]
                        : ['filter-collection-section'],
                    );

                    const schedulingClass = classnames(
                      activeScroll === 'scheduling-section'
                        ? ['scheduling', 'scheduling-focused']
                        : ['scheduling'],
                    );

                    return (
                      <>
                        <section className="source-selection-container">
                          <ul className={styles.ul_list}>
                            <li id="select-collection" className={selectCollectionClass}>
                              <div className="header-item">
                                <div className="bullet">
                                  <span>1</span>
                                </div>
                                <label>
                                  Select a Collection{' '}
                                  <Typography
                                    variant="body3"
                                    color="textSecondary"
                                    className="description"
                                  >
                                    Specify the source collection where the system retrieves data
                                    for processing.
                                  </Typography>
                                </label>
                              </div>

                              <div className="database-source-configurator-top-content">
                                <div className="locator">
                                  <span className="title">Selected Location:</span>
                                  <SelectedDatabaseInputs
                                    databasePlaceholder="Select a database bellow or type the name here"
                                    tablePlaceholder="Select a location bellow or type the name here"
                                    selectedDatabaseId={selectedDatabaseId}
                                    selectedTableId={selectedCollection?.id}
                                    selectDatabase={this.selectDatabase}
                                    selectTable={this.onChangeTableInput}
                                    databasesIds={databasesIds}
                                    rightAction={
                                      <div className="refresh-buttons-container">
                                        {this.props.isLoadingProbeData ? (
                                          <div className="refresh-loader-container">
                                            <CircularProgress
                                              size={19}
                                              sx={{
                                                color: 'currentColor',
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <RefreshIcon
                                            className="refresh-icon"
                                            fontSize="large"
                                            onClick={() => this.props.onRequestProbeTree()}
                                          />
                                        )}
                                      </div>
                                    }
                                  />
                                  {/*{locationElement}*/}
                                </div>

                                {this.props.probeError ? (
                                  <EmptyView
                                    title="There was an error loading resources"
                                    actionTitle="Try Again"
                                    actionProps={{
                                      onClick: this.onRequestTree,
                                    }}
                                  />
                                ) : null}

                                <DatabaseList
                                  credentialId={this.props.selectedCredentialId}
                                  databases={databasesArray}
                                  connectionCategory={CONNECTION_CATEGORY_NOSQL}
                                  selectedDatabaseId={selectedDatabaseId}
                                  selectDatabase={this.selectDatabase}
                                  tablesForDatabase={collectionsForDatabase}
                                  tables={collections}
                                  selectTable={this.selectCollection.bind(this)}
                                  expandedTableId={expandedCollectionId}
                                  expandTable={this.expandCollection.bind(this)}
                                  probeDataFlat={probeDataFlat}
                                  selectedTable={selectedCollection || {}}
                                  shouldShowExpandedView={false}
                                />
                              </div>
                            </li>

                            {modeField ? (
                              <li
                                key={modeField.name}
                                id="filter-collection"
                                className={filterCollectionClass}
                              >
                                <div className="header-item m_top16">
                                  <div className="bullet">
                                    <span>2</span>
                                  </div>
                                  <label>{modeField.display_name}</label>
                                </div>

                                <div className="input-filter-wrapper">
                                  <div className="input-wrapper-collection">
                                    {this.getSelectField(modeField)}
                                    <div className="call-api-button-container">
                                      <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={onGetProbeSample}
                                      >
                                        {isLoadingProbeSample ? (
                                          <CircularProgress color="primary" size={20} />
                                        ) : (
                                          'Fetch Samples'
                                        )}
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="mode-fields-container">
                                    {!isEmpty(timestampFields) && (
                                      <TimestampFields
                                        timestampFields={timestampFields}
                                        sourceConfig={sourceConfig}
                                        onChange={this.onChange}
                                      />
                                    )}

                                    {!isEmpty(queryFields) && (
                                      <div className="input-container input-query-container m_top16">
                                        <div className="query-editor-container">
                                          {map(queryFields, (field) => (
                                            <div key={field.name} className="input-row">
                                              <div className="input-holder">
                                                <label>{field.display_name}</label>
                                                <div className="monaco-editor">
                                                  <LoadableMonacoEditor
                                                    id={`nosql-editor-${field.name}`}
                                                    language={field.language}
                                                    theme={get(field, 'theme', 'dark')}
                                                    value={get(sourceConfig, field.name, '')}
                                                    height={get(field, 'height', '100%')}
                                                    onMount={(_, monaco) => {
                                                      let suggestions = [];
                                                      const {
                                                        languages: {
                                                          CompletionItemKind,
                                                          registerCompletionItemProvider,
                                                        },
                                                      } = monaco;

                                                      const mapper = (x) => ({
                                                        label: x.name,
                                                        kind: CompletionItemKind.Value,
                                                        insertText: x.name,
                                                      });

                                                      const queryMapper = (x) => ({
                                                        label: x,
                                                        kind: CompletionItemKind.Value,
                                                        insertText: x,
                                                      });

                                                      let firebaseQueryOperator = [
                                                        'LESS_THAN',
                                                        'LESS_THAN_OR_EQUAL_TO',
                                                        'EQUAL_TO',
                                                        'GREATER_THAN',
                                                        'GREATER_THAN_OR_EQUAL_TO',
                                                        'NOT_EQUAL_TO',
                                                        'IN',
                                                        'NOT_IN',
                                                      ];
                                                      let keysSuggestions = [
                                                        'filters',
                                                        'fieldName',
                                                        'operator',
                                                        'fieldType',
                                                        'stringValue',
                                                        'direction',
                                                        'ASCENDING',
                                                        'string',
                                                        'string-sequnce',
                                                        'boolean',
                                                        'boolean-sequence',
                                                        'number',
                                                        'number-sequence',
                                                      ];
                                                      suggestions = [
                                                        ...map(keysSuggestions, queryMapper),
                                                        ...map(firebaseQueryOperator, queryMapper),
                                                        ...map(collections, mapper),
                                                      ];

                                                      if (!suggestions.length) return;

                                                      return [
                                                        registerCompletionItemProvider(
                                                          field.language,
                                                          {
                                                            provideCompletionItems(
                                                              model,
                                                              position,
                                                            ) {
                                                              const word =
                                                                model.getWordUntilPosition(
                                                                  position,
                                                                );
                                                              const range = {
                                                                startLineNumber:
                                                                  position.lineNumber,
                                                                endLineNumber: position.lineNumber,
                                                                startColumn: word.startColumn,
                                                                endColumn: word.endColumn,
                                                              };

                                                              return {
                                                                suggestions: suggestions.map(
                                                                  (suggestion) => ({
                                                                    ...suggestion,
                                                                    range,
                                                                  }),
                                                                ),
                                                              };
                                                            },
                                                          },
                                                        ),
                                                      ];
                                                    }}
                                                    onChange={debounce((query) => {
                                                      onSourceConfigChanged(
                                                        this.getNewConfig({
                                                          ...sourceConfig,
                                                          [field.name]: query,
                                                        }),
                                                      );
                                                    }, 200)}
                                                    options={{
                                                      minimap: {
                                                        enabled: false,
                                                      },
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ) : null}

                            {isHCEnabled && hcScaleFactorField && (
                              <li key={hcScaleFactorField.name}>
                                <div className="input-row">
                                  <div className="input-holder">
                                    <label>{hcScaleFactorField.display_name}</label>
                                    {this.getSelectField(
                                      hcScaleFactorField.name,
                                      hcScaleFactorField.values,
                                    )}
                                  </div>
                                </div>
                              </li>
                            )}
                          </ul>
                          <section className="sample-preview-container">
                            <h1 className="sample-title">Sample Documents</h1>
                            <div className="database-samples-content">{content}</div>
                          </section>
                        </section>

                        <div id="scheduling-section">
                          <MonitoringInterval
                            source={this.props.sourceBeingEdited}
                            field={startCronField}
                            config={this.props.visibleFieldData}
                            monitorIntervalContainerClassname={schedulingClass}
                            onConfigChanged={(data, name, value) => {
                              this.onChange(name, value, data);
                            }}
                          />
                        </div>
                      </>
                    );
                  }}
                </NavScrollerContext.Consumer>
              </NavScroller>
            </div>
          </div>
        </Stack>
      </Stack>
    );
  }
}

export default NoSQLConfigurator;

function TimestampFields(props) {
  const { timestampFields, sourceConfig, onChange } = props;

  return (
    <div className="advance-fields-container">
      {map(timestampFields, (field) => {
        const { name = '', required, inputType = 'text', options } = field;

        const isSelectField = inputType === 'select';
        const currentFieldValue = isSelectField
          ? getCurrentSelectedOption(options, get(sourceConfig, name))
          : get(sourceConfig, name, '');
        const selectInputDefaultProps = isSelectField
          ? {
              labeled: false,
              isClearable: false,
              isSearchable: true,
              backspaceRemovesValue: false,
            }
          : {};

        return (
          <div key={name} className="input-container m_top16">
            <FieldBuilderRedesigned
              id={name}
              required={required}
              field={field}
              showRequiredIndicator
              fieldLabelClassName="field_label"
              currentFieldValue={currentFieldValue}
              onChange={onChange}
              {...selectInputDefaultProps}
            />
          </div>
        );
      })}
    </div>
  );
}
